import { useState } from 'react';
import { Box, IconButton, Stack, Typography, Button, Grid2 } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { postLmsSubject } from '../../assets/nodeApi/postApi/postAdminApi';
import { putLmsSubject } from '../../assets/nodeApi/putApi/putAdminApi';
import { patchSubjectTag } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { useLmsDropdownOptions } from './option/LsmOption';

const LmsSubject = () => {
    const { showToaster } = useToaster();
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { lmsSubject } = useLmsDropdownOptions()

    const validationSchema = Yup.object().shape({
        subject: Yup.string().required('Name is required'),
    });

    const initialValues = {
        subject: selectedSubject?.subject || '',
    };

    const mutationAdd = useMutation({
        mutationFn: (formData) => postLmsSubject(formData),
        onSuccess: () => {
            showToaster('Subject added successfully!', '', 'success');
            queryClient.invalidateQueries(['subject']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to add Subject', '', 'error');
        },
    });


    const mutationUpdate = useMutation({
        mutationFn: ({ formData, id }) => putLmsSubject(formData, id),
        onSuccess: () => {
            showToaster('Subject updated successfully!', '', 'success');
            queryClient.invalidateQueries(['subject']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update Subject', '', 'error');
        },
    });


    const handleSubmit = (values, { resetForm }) => {

        if (isEditing) {
            mutationUpdate.mutate({ formData: values, id: selectedSubject.id, });
        } else {
            mutationAdd.mutate(values);
            resetForm();
        }
        setIsEditing(false);
        setSelectedSubject(null);

    };

    const toggleStatusMutation = useMutation({
        mutationFn: ({ formData, id }) => patchSubjectTag(formData, id),
        onSuccess: (response) => {
            showToaster(`Subject ${response?.verify?.status==="active" ? "Enabled" : "Disabled"} successfully!`, '', 'success');
            queryClient.invalidateQueries(['subject']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update status', '', 'error');
        },
    });



    const subjectColumns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'subject', headerName: 'Subject Name', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        onClick={() => {
                            setSelectedSubject(params.row);
                            setIsEditing(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                </Stack>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch checked={params.row.status === "active" ? true : false}
                    onCheck={() =>
                        toggleStatusMutation.mutate({
                            formData: { status: params.row.status !== "active" ? "active" : "inactive" },
                            id: params.row.id
                        })
                    }
                />
            ),
        },
    ];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Subject' : 'Add Subject'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={Input_Width}>
                                    <Field name="subject" component={TextFormField} label="Subject" isRequired />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap} sx={{justifyContent:"end"}}>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3}}>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={() => {
                                            resetForm();
                                            setIsEditing(false);
                                            setSelectedSubject(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3}}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={mutationAdd.isPending || mutationUpdate.isPending}
                                    >
                                        {mutationUpdate.isPending
                                            ? "Updating..."
                                            : mutationAdd.isPending
                                                ? "Submitting..."
                                                : isEditing
                                                    ? "Update"
                                                    : "Submit"}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>

            <CustomDivider />

            <TableFilterTemplate
                title="Subject List"
                columns={subjectColumns}
                rowsData={lmsSubject.options || []}
                isLoading={lmsSubject.isLoading} />
        </Box>
    );
};

export default LmsSubject;




